import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchEventDetails, updateEvent, deleteEvent } from '../redux/EventSlice';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/Config';
import { Calendar, MapPin, Clock, Globe, ArrowLeft, Edit, Trash2, AlertCircle } from 'lucide-react';
import EditEventForm from '../components/EditEventForm'; // Import the new component

const EventDetailsPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const event = useSelector(state => state.events.selectedEvent);
  const loading = useSelector(state => state.events.loading);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // New state for edit modal

  useEffect(() => {
    if (id) {
      dispatch(fetchEventDetails(id))
        .unwrap()
        .catch(error => {
          toast.error(error.message || 'Failed to load event details');
          navigate('/dashboard/events');
        });
    }
    
    // Cleanup function to reset selected event when leaving the page
    return () => {
      dispatch({ type: 'eventsSlice/setSelectedEvent', payload: null });
    };
  }, [id, dispatch, navigate]);

  const handleEditEvent = () => {
    setIsEditModalOpen(true); // Open the edit modal instead of navigating
  };

  const handleEditSuccess = () => {
    // Refresh event details after successful edit
    dispatch(fetchEventDetails(id));
  };

  const handleDeleteEvent = () => {
    dispatch(deleteEvent(id))
      .unwrap()
      .then(() => {
        toast.success('Event deleted successfully');
        navigate('/dashboard/events');
      })
      .catch(error => {
        toast.error(error.message || 'Failed to delete event');
      })
      .finally(() => {
        setIsDeleteModalOpen(false);
      });
  };

  // Function to determine badge color based on status
  const getBadgeColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'ongoing':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Event not found</h2>
          <button
            onClick={() => navigate('/dashboard/events')}
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Back button and actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <button
          onClick={() => navigate('/dashboard/events')}
          className="inline-flex items-center text-blue-500 hover:text-blue-700 mb-4 sm:mb-0"
        >
          <ArrowLeft size={18} className="mr-1" />
          Back to Events
        </button>
        
        <div className="flex space-x-3">
          <button
            onClick={handleEditEvent}
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <Edit size={18} className="mr-2" />
            Edit
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            <Trash2 size={18} className="mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Event Details Card */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Event Image */}
        <div className="relative w-full h-64 sm:h-80">
          <img
            src={event.img || '/api/placeholder/800/400'}
            alt={event.title}
            className="w-full h-full object-contain"
          />
          <span className={`absolute top-4 right-4 px-3 py-1 text-sm font-medium rounded-full ${getBadgeColor(event.status)}`}>
            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
          </span>
        </div>
        
        {/* Event Details */}
        <div className="p-6">
          <h1 className="text-3xl font-bold mb-4">{event.title}</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-blue-500 mt-1 mr-3" />
                <div>
                  <h3 className="text-gray-600 text-sm">Date</h3>
                  <p>{formatDate(event.start_date)}{event.end_date !== event.start_date ? ` - ${formatDate(event.end_date)}` : ''}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <Clock className="w-5 h-5 text-blue-500 mt-1 mr-3" />
                <div>
                  <h3 className="text-gray-600 text-sm">Time</h3>
                  <p>{event.time || 'Not specified'}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-blue-500 mt-1 mr-3" />
                <div>
                  <h3 className="text-gray-600 text-sm">Location</h3>
                  <p>{event.location || 'Not specified'}</p>
                </div>
              </div>
              
              {event.link && (
                <div className="flex items-start">
                  <Globe className="w-5 h-5 text-blue-500 mt-1 mr-3" />
                  <div>
                    <h3 className="text-gray-600 text-sm">Event Link</h3>
                    <a 
                      href={event.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      {event.link}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="border-t pt-6 mt-6">
            <h2 className="text-xl font-semibold mb-4">Description</h2>
            <p className="text-gray-700 whitespace-pre-line">
              {event.description || 'No description provided.'}
            </p>
          </div>
          
          <div className="border-t pt-4 mt-6 text-sm text-gray-500">
            <p>Created: {formatDate(event.created_at)}</p>
            <p>Last Updated: {formatDate(event.updated_at)}</p>
          </div>
        </div>
      </div>

      {/* Edit Event Modal */}
      {isEditModalOpen && (
        <EditEventForm 
          event={event} 
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-center text-red-500 mb-4">
              <AlertCircle size={24} className="mr-2" />
              <h3 className="text-lg font-semibold">Confirm Deletion</h3>
            </div>
            <p className="mb-6">
              Are you sure you want to delete this event? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEvent}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailsPage;